// Lib
import { useCallback, useLayoutEffect, useState } from 'react';

// Types
import {
    INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS,
    type BackgroundRemovalProgress,
} from '../../toolbar/imageModalBackgroundRemovalProgressTypes';
import {
    IMAGE_MODAL_BACKGROUND_REMOVAL_TOAST_MESSAGE,
    type ImageModalBackgroundRemovalToastState,
} from '../../toolbar/imageModalBackgroundRemovalToastTypes';

const IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE = {
    IDLE: 'idle',
    REMOVING_BACKGROUND: 'removingBackground',
    UPLOADING_PROCESSED_IMAGE: 'uploadingProcessedImage',
    WAITING_FOR_EDITOR_IMAGE: 'waitingForEditorImage',
} as const;

type ImageModalV2BackgroundRemovalPhase =
    (typeof IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE)[keyof typeof IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE];

type EditorImageReloadState = {
    initialImageLoadKey: string;
    status: 'waitingForImageUpdate' | 'waitingForEditorReady';
};

const getBackgroundRemovalPhase = ({
    backgroundRemovalApiProgress,
    backgroundRemovalUploadProgress,
    editorImageReload,
}: {
    backgroundRemovalApiProgress: BackgroundRemovalProgress | null;
    backgroundRemovalUploadProgress: BackgroundRemovalProgress | null;
    editorImageReload: EditorImageReloadState | null;
}): ImageModalV2BackgroundRemovalPhase => {
    if (backgroundRemovalUploadProgress) return IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE.UPLOADING_PROCESSED_IMAGE;
    if (backgroundRemovalApiProgress) return IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE.REMOVING_BACKGROUND;
    if (editorImageReload) return IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE.WAITING_FOR_EDITOR_IMAGE;

    return IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE.IDLE;
};

const getBackgroundRemovalToast = ({
    phase,
    backgroundRemovalApiProgress,
    backgroundRemovalUploadProgress,
}: {
    phase: ImageModalV2BackgroundRemovalPhase;
    backgroundRemovalApiProgress: BackgroundRemovalProgress | null;
    backgroundRemovalUploadProgress: BackgroundRemovalProgress | null;
}): ImageModalBackgroundRemovalToastState | null => {
    switch (phase) {
        case IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE.REMOVING_BACKGROUND:
            return {
                message: IMAGE_MODAL_BACKGROUND_REMOVAL_TOAST_MESSAGE.REMOVING_BACKGROUND,
                progress: backgroundRemovalApiProgress,
            };

        case IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE.UPLOADING_PROCESSED_IMAGE:
            return {
                message: IMAGE_MODAL_BACKGROUND_REMOVAL_TOAST_MESSAGE.UPLOADING_IMAGE,
                progress: backgroundRemovalUploadProgress,
            };

        case IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE.WAITING_FOR_EDITOR_IMAGE:
            return {
                message: IMAGE_MODAL_BACKGROUND_REMOVAL_TOAST_MESSAGE.UPLOADING_IMAGE,
                progress: INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS,
            };

        default:
            return null;
    }
};

const getBackgroundRemovalButtonProgress = ({
    phase,
    backgroundRemovalApiProgress,
    backgroundRemovalUploadProgress,
}: {
    phase: ImageModalV2BackgroundRemovalPhase;
    backgroundRemovalApiProgress: BackgroundRemovalProgress | null;
    backgroundRemovalUploadProgress: BackgroundRemovalProgress | null;
}): BackgroundRemovalProgress | null => {
    if (backgroundRemovalApiProgress || backgroundRemovalUploadProgress) {
        return backgroundRemovalUploadProgress || backgroundRemovalApiProgress;
    }

    if (phase === IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE.WAITING_FOR_EDITOR_IMAGE)
        return INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS;

    return null;
};

/**
 * Move the pending editor image reload through image replacement and editor readiness.
 */
const getNextEditorImageReload = ({
    editorImageLoadKey,
    editorImageReload,
    editorIsReady,
    hasBackgroundRemovalProgress,
}: {
    editorImageLoadKey: string;
    editorImageReload: EditorImageReloadState;
    editorIsReady: boolean;
    hasBackgroundRemovalProgress: boolean;
}): EditorImageReloadState | null => {
    const hasEditorImageChanged = editorImageReload.initialImageLoadKey !== editorImageLoadKey;

    if (!hasEditorImageChanged) return hasBackgroundRemovalProgress ? editorImageReload : null;

    if (editorImageReload.status === 'waitingForImageUpdate') {
        return editorIsReady ? editorImageReload : { ...editorImageReload, status: 'waitingForEditorReady' };
    }

    return editorIsReady ? null : editorImageReload;
};

/**
 * Models the v2 background-removal workflow as one phase rather than separate UI booleans.
 */
export const useImageModalV2BackgroundRemovalState = ({
    editorImageLoadKey,
    editorIsReady,
    backgroundRemovalApiProgress,
    backgroundRemovalUploadProgress,
}: {
    editorImageLoadKey: string;
    editorIsReady: boolean;
    backgroundRemovalApiProgress: BackgroundRemovalProgress | null;
    backgroundRemovalUploadProgress: BackgroundRemovalProgress | null;
}) => {
    const [hasLoadedEditorImage, setHasLoadedEditorImage] = useState(false);
    const [editorImageReload, setEditorImageReload] = useState<EditorImageReloadState | null>(null);
    const hasBackgroundRemovalProgress = !!backgroundRemovalApiProgress || !!backgroundRemovalUploadProgress;

    useLayoutEffect(() => {
        if (!backgroundRemovalUploadProgress) return;

        setEditorImageReload(
            (current) =>
                current || {
                    initialImageLoadKey: editorImageLoadKey,
                    status: 'waitingForImageUpdate',
                },
        );
    }, [backgroundRemovalUploadProgress, editorImageLoadKey]);

    useLayoutEffect(() => {
        if (!editorImageReload) return;

        setEditorImageReload(
            getNextEditorImageReload({
                editorImageLoadKey,
                editorImageReload,
                editorIsReady,
                hasBackgroundRemovalProgress,
            }),
        );
    }, [editorImageLoadKey, editorImageReload, editorIsReady, hasBackgroundRemovalProgress]);

    const handleEditorImageReady = useCallback((ready: boolean) => {
        if (!ready) return;

        setHasLoadedEditorImage(true);
    }, []);

    const phase = getBackgroundRemovalPhase({
        backgroundRemovalApiProgress,
        backgroundRemovalUploadProgress,
        editorImageReload,
    });
    const isProcessing = phase !== IMAGE_MODAL_V2_BACKGROUND_REMOVAL_PHASE.IDLE;

    return {
        phase,
        buttonProgress: getBackgroundRemovalButtonProgress({
            phase,
            backgroundRemovalApiProgress,
            backgroundRemovalUploadProgress,
        }),
        controlsDisabled: isProcessing,
        handleEditorImageReady,
        shouldAnimateEditorImageReplacement: isProcessing,
        shouldHideToolbar: isProcessing,
        shouldShowEditorLoadingPreview: !editorIsReady && !hasLoadedEditorImage,
        toast: getBackgroundRemovalToast({
            phase,
            backgroundRemovalApiProgress,
            backgroundRemovalUploadProgress,
        }),
    };
};
